// Client-side allowlist validator for theme.launcherCustomCSS.
//
// Mirrors the backend tinycss2-based allowlist in
// ai-agent-backend/src/app/services/launcher_css_validator.py. Provides fast
// inline feedback in the backoffice; the backend remains the authoritative
// validator (returns 422 on save).
//
// Allowed:
//   - @keyframes (any name, including overriding kodly-ai-agent-* presets)
//   - @media / @supports — recurse into nested cssRules under the same rules
//   - Style rules anchored on the launcher class (`.kodly-ai-agent-launcher`,
//     or a BEM `__element` / `--modifier` of it). Pseudos and attribute
//     selectors are fine; descendant ( ) and child (>) combinators stay inside
//     the launcher subtree and are allowed.
//
// Blocked:
//   - Any selector not anchored on the launcher class (`body`, `html`, `*`,
//     `:root`, `:host`, a standalone `[data-foo]`, or a different class that
//     merely shares the prefix such as `.kodly-ai-agent-launcher-wrapper`)
//   - Sibling combinators (`~`, `+`): the launcher is a host-page DOM sibling,
//     so they would reach the embedding site's own elements
//   - At-rules that surface as rules here: `@font-face`, `@layer`, `@page`, etc.
//
// Not caught here — the backend stays authoritative: anything
// `CSSStyleSheet.replaceSync` silently drops without creating a rule (bad
// syntax, `@import`, `@charset`) never reaches this layer.

const LAUNCHER_PREFIX = ".kodly-ai-agent-launcher";
const LAUNCHER_CLASS = LAUNCHER_PREFIX.slice(1); // class name without the leading dot

export function validateLauncherCss(css) {
  if (!css || !css.trim()) return { ok: true };
  let sheet;
  try {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
  } catch (e) {
    return { ok: false, error: `CSS parse error: ${e.message}` };
  }
  return validateRules(sheet.cssRules);
}

function validateRules(rules) {
  for (const rule of rules) {
    const result = validateRule(rule);
    if (!result.ok) return result;
  }
  return { ok: true };
}

function validateRule(rule) {
  // @keyframes — allowed regardless of name (lets tenants override presets).
  if (typeof CSSKeyframesRule !== "undefined" && rule instanceof CSSKeyframesRule) {
    return { ok: true };
  }
  // @media — recurse into nested rules under the same allowlist.
  if (typeof CSSMediaRule !== "undefined" && rule instanceof CSSMediaRule) {
    return validateRules(rule.cssRules);
  }
  // @supports — recurse.
  if (typeof CSSSupportsRule !== "undefined" && rule instanceof CSSSupportsRule) {
    return validateRules(rule.cssRules);
  }
  // Style rules — every selector in the list must be anchored to the launcher
  // class, with no sibling combinator escaping the launcher's subtree.
  if (typeof CSSStyleRule !== "undefined" && rule instanceof CSSStyleRule) {
    for (const sub of splitTopLevelSelectors(rule.selectorText)) {
      const trimmed = sub.trim();
      if (!selectorIsLauncherScoped(trimmed)) {
        return {
          ok: false,
          error: `selector \`${trimmed}\` is not allowed — must be scoped to \`${LAUNCHER_PREFIX}\` (descendant/child only, no sibling combinators)`,
        };
      }
    }
    return { ok: true };
  }
  // Anything else (@font-face, @layer, @container, @page, @property, etc.) —
  // reject. Extract the at-rule token directly from cssText; the leading
  // `@token` is reliably parseable from any at-rule's serialized form and
  // matches the backend's "@xxx is not allowed" message.
  const match = rule.cssText && rule.cssText.match(/^@[\w-]+/);
  const token = match ? match[0] : "<unknown at-rule>";
  return { ok: false, error: `${token} is not allowed` };
}

// True if `name` is the launcher class itself or a BEM __element / --modifier
// of it. Mirrors the backend's _is_launcher_class — note a class that merely
// shares the prefix (e.g. `kodly-ai-agent-launcher-wrapper`) is NOT a match.
function isLauncherClass(name) {
  return (
    name === LAUNCHER_CLASS ||
    name.startsWith(`${LAUNCHER_CLASS}__`) ||
    name.startsWith(`${LAUNCHER_CLASS}--`)
  );
}

// True if every element a selector can match is the launcher or a descendant.
// The leftmost compound must be the launcher class; the anchor may connect to
// further compounds only via a descendant ( ) or child (>) combinator. Sibling
// combinators (~, +) are rejected because the launcher is a host-page DOM
// sibling, so they would match the embedding site's own elements. Once we
// descend via ` ` or `>`, every later match stays inside the launcher subtree,
// so the rest needs no check. Mirrors the backend's _selector_is_launcher_scoped.
// (selectorText is already normalised by the CSSOM, but combinator chars are
// detected regardless of the surrounding whitespace.)
function selectorIsLauncherScoped(selector) {
  const m = /^\.([A-Za-z0-9_-]+)/.exec(selector);
  if (!m || !isLauncherClass(m[1])) return false;
  // Scan the remainder for the first top-level combinator, skipping over
  // [...]/(...) blocks and quoted strings so characters inside them (e.g. a
  // space in `[aria-label="Open chat"]`) are not read as combinators.
  const rest = selector.slice(m[0].length);
  let depth = 0;
  let quote = null;
  let seenWhitespace = false;
  for (let i = 0; i < rest.length; i++) {
    const ch = rest[i];
    if (quote) {
      if (ch === quote && rest[i - 1] !== "\\") quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if (ch === "[" || ch === "(") {
      if (depth === 0 && seenWhitespace) return true; // descendant reached this block
      depth += 1;
      continue;
    }
    if (ch === "]" || ch === ")") {
      depth -= 1;
      continue;
    }
    if (depth > 0) continue;
    if (ch === ">") return true; // child combinator: stays in the launcher subtree
    if (ch === "~" || ch === "+") return false; // sibling combinator: escapes it
    if (/\s/.test(ch)) {
      seenWhitespace = true;
      continue;
    }
    if (seenWhitespace) return true; // descendant combinator: rest is inside the subtree
  }
  return true;
}

// Splits a selector list on top-level commas (ignoring commas inside `(...)`
// and `[...]`, so things like `:is(a, b)` or `[data-x=","]` stay intact).
function splitTopLevelSelectors(text) {
  let depth = 0;
  const parts = [];
  let buf = "";
  for (const ch of text) {
    if (ch === "(" || ch === "[") depth++;
    else if (ch === ")" || ch === "]") depth--;
    if (ch === "," && depth === 0) {
      parts.push(buf);
      buf = "";
    } else {
      buf += ch;
    }
  }
  if (buf) parts.push(buf);
  return parts;
}
